import { baseImageToOss, generateRandomCode } from './common';
import { request } from './request';
import {OSS_BUCKET, OSS_BASE_URL} from '../config';

// 图片类型定义
export interface ImageItem {
  id: number;
  name: string;
  size: string;
  uploadDate: string;
  type: string;
  isPublic: boolean;
  views: number;
  downloads: number;
  url?: string;
}

// 获取文件扩展名的辅助函数
export const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return '.jpg'; // 默认扩展名
  }
  return filename.substring(lastDotIndex);
};

// 上传图片到OSS并保存到数据库
export const uploadImageToOSSAndDB = async (
  file: File,
  imageType: string = 'background'
): Promise<ImageItem> => {
  return new Promise((resolve, reject) => {
    // 将文件转换为base64
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64String = event.target?.result as string;
      
      try {
        // 调用 baseImageToOss 上传到OSS，使用实际文件扩展名
        const uploadedName = await baseImageToOss(
          generateRandomCode() + getFileExtension(file.name), 
          base64String, 
          OSS_BUCKET
        );
      
        console.log('上传成功:', uploadedName);
        
        // 创建新的图片项，使用OSS返回的URL
        const newImage: ImageItem = {
          id: Date.now(),
          name: file.name,
          size: `${(file.size / 1024).toFixed(1)} KB`,
          uploadDate: new Date().toISOString(),
          type: imageType,
          isPublic: true,
          views: 0,
          downloads: 0,
          url: `${OSS_BASE_URL}${uploadedName}` // 使用OSS URL
        };
        
        // 提交图片到数据库
        try {
          await request({
            url: 'images/add',
            method: 'POST',
            data: {
              images: [{
                name: newImage.name,
                url: newImage.url,
                size: newImage.size,
                type: imageType,
                isPublic: newImage.isPublic,
                views: newImage.views,
                downloads: newImage.downloads
              }]
            }
          });
          console.log('Image added to database successfully');
        } catch (dbError) {
          console.error('Failed to add image to database:', dbError);
          // 即使数据库保存失败，也返回图片信息
        }
        
        resolve(newImage);
        
      } catch (uploadError) {
        console.error('上传到OSS失败:', uploadError);
        // 如果上传失败，仍然使用本地URL作为备选
        const imageUrl = URL.createObjectURL(file);
        const newImage: ImageItem = {
          id: Date.now(),
          name: file.name,
          size: `${(file.size / 1024).toFixed(1)} KB`,
          uploadDate: new Date().toISOString(),
          type: imageType,
          isPublic: true,
          views: 0,
          downloads: 0,
          url: imageUrl
        };
        
        // 提交图片到数据库（即使上传失败，也保存本地URL）
        try {
          await request({
            url: 'images/add',
            method: 'POST',
            data: {
              images: [{
                name: newImage.name,
                url: newImage.url,
                size: newImage.size,
                type: imageType,
                isPublic: newImage.isPublic,
                views: newImage.views,
                downloads: newImage.downloads
              }]
            }
          });
          console.log('Image added to database successfully (fallback)');
        } catch (dbError) {
          console.error('Failed to add image to database (fallback):', dbError);
        }
        
        resolve(newImage);
      }
    };
    
    reader.readAsDataURL(file);
  });
};

// 从API加载图片数据
export const loadImagesFromAPI = async (
  page: number = 1, 
  search: string = '', 
  type: string = 'background',
  imagesPerPage: number = 12,
  tags?: (string | number)[]
): Promise<{images: ImageItem[], total: number, totalPages: number}> => {
  try {
    const params: any = {
      page,
      limit: imagesPerPage
    };
    
    if (search.trim()) {
      params.search = search.trim();
    }
    
    params.type = type;
    if (tags && tags.length) {
      params.tags = Array.from(new Set(tags.map(String))).join(',');
    }
    
    const response: any = await request({
      url: 'images/list',
      method: 'GET',
      params
    });
    
    if (response.success && response.result) {
      const imagesData = response.result.map((item: any) => ({
        id: item.id,
        name: item.name,
        size: item.size,
        uploadDate: item.uploadDate,
        type: item.type,
        isPublic: item.isPublic,
        views: item.views,
        downloads: item.downloads,
        url: item.url
      }));
      
      return {
        images: imagesData,
        total: response.total || imagesData.length,
        totalPages: response.totalPages || Math.ceil((response.total || imagesData.length) / imagesPerPage)
      };
    }
    return { images: [], total: 0, totalPages: 0 };
  } catch (error) {
    console.error('Error loading images from API:', error);
    return { images: [], total: 0, totalPages: 0 };
  }
};

// 批量上传图片
export const uploadMultipleImages = async (
  files: FileList,
  imageType: string = 'background'
): Promise<ImageItem[]> => {
  const uploadPromises = Array.from(files).map(file => 
    uploadImageToOSSAndDB(file, imageType)
  );
  
  try {
    const results = await Promise.all(uploadPromises);
    return results;
  } catch (error) {
    console.error('Batch upload failed:', error);
    throw error;
  }
};
